import React, { useState, useEffect } from 'react';
import { 
  Save, 
  Plus, 
  Trash2, 
  Settings,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { servicesAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import {
  ServicesContainer,
  FloatingElement,
  MainContent,
  SectionHeader,
  Title,
  Subtitle,
  Form,
  FormSection,
  SectionTitle,
  InputGroup,
  Label,
  Input,
  TextArea,
  ServicesGrid,
  ServiceCard,
  ServiceHeader,
  ServiceTitle,
  ServiceActions,
  ActionButton,
  BulletPointsContainer,
  BulletPoint,
  BulletInput,
  AddServiceButton,
  SubmitButton,
  LoadingSpinner,
  ErrorMessage,
  SuccessMessage,
  PreviewSection,
  PreviewTitle,
  PreviewCard,
  PreviewContent,
  PreviewServices,
  PreviewService,
  PreviewServiceTitle,
  PreviewServiceDescription,
  PreviewBulletPoints,
  PreviewBulletPoint,
  PreviewButton,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText
} from './ServicesSection.styles';

const ServicesSection = () => {
  const { showSuccess, showError } = useToast();
  const [formData, setFormData] = useState({
    overall_heading: '',
    overall_explanation: '',
    services: []
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [existingData, setExistingData] = useState(null);

  useEffect(() => {
    loadServicesSection();
  }, []);

  const loadServicesSection = async () => {
    try {
      setLoading(true);
      const response = await servicesAPI.get();
      if (response.status === 200) {
        const data = response.data;
        setExistingData(data);
        setFormData({
          overall_heading: data.overall_heading || '',
          overall_explanation: data.overall_explanation || '',
          services: data.services || []
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        const errorMessage = error.response?.data?.message || 'Failed to load services data';
        showError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleServiceChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.map((service, i) => 
        i === index ? { ...service, [field]: value } : service
      )
    }));
  };

  const handleBulletPointChange = (serviceIndex, bulletIndex, value) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.map((service, i) => 
        i === serviceIndex 
          ? {
              ...service,
              bullet_points: service.bullet_points.map((bullet, j) => 
                j === bulletIndex ? value : bullet
              )
            }
          : service
      )
    }));
  };

  const addService = () => {
    const newService = {
      service_name: '',
      brief_explanation: '',
      bullet_points: ['', '', '', ''],
      button_text: ''
    };
    
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, newService]
    }));
  };

  const removeService = (index) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.overall_heading.trim() || !formData.overall_explanation.trim()) {
      setError('Please fill in the overall heading and explanation.');
      return;
    }

    if (formData.services.length === 0) {
      setError('Please add at least one service.');
      return;
    }

    // Validate each service
    for (let i = 0; i < formData.services.length; i++) {
      const service = formData.services[i];
      if (!service.service_name.trim() || !service.brief_explanation.trim() || !service.button_text.trim()) {
        setError(`Service ${i + 1}: Please fill in all required fields.`);
        return;
      }
      
      // Check if all bullet points are filled
      const emptyBullets = service.bullet_points.filter(bullet => !bullet.trim());
      if (emptyBullets.length > 0) {
        setError(`Service ${i + 1}: Please fill in all 4 bullet points.`);
        return;
      }
    }

    try {
      setSaving(true);
      setError('');

      const response = existingData 
        ? await servicesAPI.update(formData)
        : await servicesAPI.create(formData);

      if (response.status === 200 || response.status === 201) {
        showSuccess(existingData ? 'Services section updated successfully!' : 'Services section created successfully!');
        setExistingData(response.data);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save services section';
      showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !existingData) {
    return (
      <ServicesContainer>
        <MainContent>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '400px',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <LoadingSpinner />
            <p style={{ color: '#64748B', fontSize: '1rem' }}>Loading services data...</p>
          </div>
        </MainContent>
      </ServicesContainer>
    );
  }

  return (
    <ServicesContainer>
      <FloatingElement delay="0s" />
      <FloatingElement delay="3s" />
      <FloatingElement delay="6s" />

      <MainContent>
        <SectionHeader>
          <Title>Services Section</Title>
          <Subtitle>
            Manage your company's services content, including descriptions and key features.
          </Subtitle>
        </SectionHeader>

        {error && (
          <ErrorMessage>
            <AlertCircle size={20} />
            {error}
          </ErrorMessage>
        )}

        <Form onSubmit={handleSubmit}>
          <FormSection>
            <SectionTitle>Overall Section</SectionTitle>
            
            <InputGroup>
              <Label htmlFor="overall_heading">Overall Heading *</Label>
              <Input
                id="overall_heading"
                type="text"
                value={formData.overall_heading}
                onChange={(e) => handleInputChange('overall_heading', e.target.value)}
                placeholder="Enter the main heading for your services section"
                required
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="overall_explanation">Overall Explanation *</Label>
              <TextArea
                id="overall_explanation"
                value={formData.overall_explanation}
                onChange={(e) => handleInputChange('overall_explanation', e.target.value)}
                placeholder="Write a brief explanation that introduces your services section..."
                rows={4}
                required
              />
            </InputGroup>
          </FormSection>

          <FormSection>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <SectionTitle>Services</SectionTitle>
              <AddServiceButton type="button" onClick={addService}>
                <Plus size={16} />
                Add Service
              </AddServiceButton>
            </div>

            {formData.services.length === 0 ? (
              <EmptyState>
                <EmptyStateIcon>
                  <Settings size={48} />
                </EmptyStateIcon>
                <EmptyStateText>No services added yet. Click "Add Service" to get started.</EmptyStateText>
              </EmptyState>
            ) : (
              <ServicesGrid>
                {formData.services.map((service, index) => (
                  <ServiceCard key={index}>
                    <ServiceHeader>
                      <ServiceTitle>Service {index + 1}</ServiceTitle>
                      <ServiceActions>
                        <ActionButton 
                          type="button" 
                          onClick={() => removeService(index)}
                          variant="danger"
                        >
                          <Trash2 size={16} />
                        </ActionButton>
                      </ServiceActions>
                    </ServiceHeader>

                    <InputGroup>
                      <Label>Service Name *</Label>
                      <Input
                        type="text"
                        value={service.service_name}
                        onChange={(e) => handleServiceChange(index, 'service_name', e.target.value)}
                        placeholder="Enter service name"
                        required
                      />
                    </InputGroup>

                    <InputGroup>
                      <Label>Brief Explanation *</Label>
                      <TextArea
                        value={service.brief_explanation}
                        onChange={(e) => handleServiceChange(index, 'brief_explanation', e.target.value)}
                        placeholder="Brief description of the service..."
                        rows={3}
                        required
                      />
                    </InputGroup>

                    <BulletPointsContainer>
                      <Label>Bullet Points * (4 required)</Label>
                      {service.bullet_points.map((bullet, bulletIndex) => (
                        <BulletPoint key={bulletIndex}>
                          <span style={{ color: '#C5337B', fontWeight: 'bold' }}>•</span>
                          <BulletInput
                            type="text"
                            value={bullet}
                            onChange={(e) => handleBulletPointChange(index, bulletIndex, e.target.value)}
                            placeholder={`Feature ${bulletIndex + 1}`}
                            required
                          />
                        </BulletPoint>
                      ))}
                    </BulletPointsContainer>

                    <InputGroup>
                      <Label>Button Text *</Label>
                      <Input
                        type="text"
                        value={service.button_text}
                        onChange={(e) => handleServiceChange(index, 'button_text', e.target.value)}
                        placeholder="e.g., Learn More, Get Started"
                        required
                      />
                    </InputGroup>
                  </ServiceCard>
                ))}
              </ServicesGrid>
            )}
            
            {formData.services.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
                <AddServiceButton type="button" onClick={addService}>
                  <Plus size={16} />
                  Add Another Service
                </AddServiceButton>
              </div>
            )}
          </FormSection>

          <SubmitButton type="submit" disabled={saving}>
            {saving ? (
              <>
                <LoadingSpinner />
                {existingData ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <Save size={20} />
                {existingData ? 'Update Services' : 'Create Services'}
              </>
            )}
          </SubmitButton>
        </Form>

        {existingData && (
          <PreviewSection>
            <PreviewTitle>Preview</PreviewTitle>
            <PreviewCard>
              <PreviewContent>
                <h2 style={{ color: '#2B3857', marginBottom: '1rem' }}>
                  {formData.overall_heading}
                </h2>
                <p style={{ marginBottom: '2rem', lineHeight: '1.6', color: '#64748B' }}>
                  {formData.overall_explanation}
                </p>
                
                <PreviewServices>
                  {formData.services.map((service, index) => (
                    <PreviewService key={index}>
                      <PreviewServiceTitle>{service.service_name}</PreviewServiceTitle>
                      <PreviewServiceDescription>{service.brief_explanation}</PreviewServiceDescription>
                      
                      <PreviewBulletPoints>
                        {service.bullet_points.map((bullet, bulletIndex) => (
                          <PreviewBulletPoint key={bulletIndex}>
                            {bullet}
                          </PreviewBulletPoint>
                        ))}
                      </PreviewBulletPoints>
                      
                      <PreviewButton>{service.button_text}</PreviewButton>
                    </PreviewService>
                  ))}
                </PreviewServices>
              </PreviewContent>
            </PreviewCard>
          </PreviewSection>
        )}

      </MainContent>
    </ServicesContainer>
  );
};

export default ServicesSection;
